import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import Spinner from '../../../components/bootstrap/Spinner';
import Alert from '../../../components/bootstrap/Alert';
import AuthContext from '../../../contexts/authContext';
import { getPageQuery } from '../../../helpers/oktaUtils';

/**
 * Okta OAuth 回调页面
 * 处理 /implicit/callback 路由
 */
const OktaCallback: React.FC = () => {
  const { handleOktaLogin, isAuthenticated, authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      const params = getPageQuery();
      const code = params.code as string;
      const errorParam = params.error as string;
      const errorDescription = params.error_description as string;

      if (errorParam) {
        setError(errorDescription || errorParam);
        setProcessing(false);
        return;
      }

      if (code) {
        try {
          await handleOktaLogin(code);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Login failed');
        }
      } else {
        setError('No authorization code received');
      }
      setProcessing(false);
    };

    processCallback();
  }, [handleOktaLogin]);

  // 认证成功后跳转
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  return (
    <PageWrapper isProtected={false} title='Processing Login'>
      <Page className='p-0'>
        <div className='row h-100 align-items-center justify-content-center'>
          <div className='col-xl-4 col-lg-6 col-md-8 text-center'>
            <Card className='shadow-3d-dark'>
              <CardBody className='p-5'>
                {processing || authLoading ? (
                  <>
                    <Spinner size='3rem' />
                    <div className='mt-4 h5'>Processing Okta login...</div>
                    <div className='text-muted'>Please wait while we verify your credentials.</div>
                  </>
                ) : error ? (
                  <>
                    <Alert color='danger' isLight icon='Error'>
                      <div className='h6'>Login Failed</div>
                      <div>{error}</div>
                    </Alert>
                    <button
                      type='button'
                      className='btn btn-primary mt-3'
                      onClick={() => navigate('/auth-pages/login', { replace: true })}>
                      Back to Login
                    </button>
                  </>
                ) : (
                  <>
                    <div className='text-success h5'>Login successful!</div>
                    <div className='text-muted'>Redirecting...</div>
                  </>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      </Page>
    </PageWrapper>
  );
};

export default OktaCallback;
